/**
 * nativeAddProjectData — data + create bridge on `window` for the native iOS
 * New Project form (IOSNewProjectForm.swift).
 *
 * The native form is SwiftUI, but its data reads AND the create flow stay
 * single-sourced in the web app (no native data layer, no drift). The native
 * side pulls read data on demand via `evaluateJavaScript` against the MAIN
 * webview, and submits by calling `createProject(payload)`, which reuses the
 * SAME mapper + services as the web form and posts the result back over the
 * bridge (`addProjectResult`).
 *
 * Install after auth (see AuthenticatedController). No-op off-web.
 */

import { DataService } from './DataService';
import { ProjectFoldersService } from './ProjectFoldersService';
import { NativeBridge } from './NativeBridge';
import { FORM_OF_CONTRACT_OPTIONS, RESOURCE_COST_OPTIONS } from '../features/projects/add-project-form/constants';
import { buildNewProjectFromFormData, deriveServiceType } from '../features/projects/add-project-form/buildNewProject';
import { EMPTY_PROJECT_LOCATION, INITIAL_FORM_DATA } from '../features/projects/add-project-form/types';
import type { FormData } from '../features/projects/add-project-form/types';
import type { Company, MainContractor } from '../types/database';
import { APPOINTMENT_NAMES, appointmentIsValidForSide } from '../contract-administration/roleProfile';
import { PROJECT_ROLES_BY_SIDE, PROJECT_ROLE_VALUES } from '../contract-administration/types';

interface NativeContractor {
  id: string;
  name: string;
}

interface NativeProjectUser {
  id: string;
  name: string;
  jobTitle: string;
  email: string;
  phone: string;
}

interface NativeOption {
  value: string;
  label: string;
}

export interface NativeAddProjectDataAPI {
  /** Contractor companies (our clients) the project can be created for. */
  getContractors(): NativeContractor[];
  /** Active users in a contractor's workspace — drives the PM/Supervisor pickers. */
  getUsers(companyId: string): NativeProjectUser[];
  /** Employer (main contractor) companies for the Employer section. */
  getMainContractors(): NativeContractor[];
  /** A main contractor's contacts — drives the Estimator/Site Manager/PQS pickers. */
  getMainContractorContacts(mainContractorId: string): NativeProjectUser[];
  /** Static option lists (single-sourced from web constants so native never drifts). */
  getFormOptions(): {
    formOfContract: NativeOption[];
    resourceCost: NativeOption[];
    appointments: NativeOption[];
    appointmentsBySide: Record<'contractor' | 'employer', NativeOption[]>;
  };
  /**
   * Create the project from a FormData-shaped payload (the same fields the web
   * form collects). Reuses buildNewProjectFromFormData + createProjectAsync +
   * ProjectFoldersService, then posts `addProjectResult { ok, projectId?, error? }`.
   */
  createProject(payload: Partial<FormData>): Promise<void>;
}

// Latest authenticated user, captured at install — createProject stamps
// createdBy / assignedStaffIds with it.
let currentUser: { id: string } | null = null;

export function installNativeAddProjectData(user: { id: string } | null): void {
  if (typeof window === 'undefined') return;
  currentUser = user;

  const api: NativeAddProjectDataAPI = {
    getContractors() {
      // "Contractor" = a client company (per Quantis terminology the client IS
      // the contractor). Mirrors ContractorTab's `clients` filter.
      return (DataService.getCompanies() as Company[])
        .filter((c) => c.type === 'client' && c.status === 'active')
        .map((c) => ({ id: c.id, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    getUsers(companyId: string) {
      if (!companyId) return [];
      return (DataService.getUsersByCompany(companyId) as any[])
        .filter((u) => u.status === 'active')
        .map((u) => ({
          id: u.id,
          name: u.name || u.email || u.phone || 'Unnamed contact',
          jobTitle: u.jobTitle || '',
          email: u.email || '',
          phone: u.phone || '',
        }));
    },
    getMainContractors() {
      return (DataService.getMainContractors() as MainContractor[])
        .filter((mc) => mc.status === 'active')
        .map((mc) => ({ id: mc.id, name: mc.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    getMainContractorContacts(mainContractorId: string) {
      if (!mainContractorId) return [];
      const mc = (DataService.getMainContractors() as MainContractor[]).find(
        (m) => m.id === mainContractorId,
      );
      return ((mc?.contacts ?? []) as any[]).map((c) => ({
        id: c.id,
        name: c.name || c.email || c.phone || 'Unnamed contact',
        jobTitle: c.position || '',
        email: c.email || '',
        phone: c.phone || '',
      }));
    },
    getFormOptions() {
      return {
        formOfContract: FORM_OF_CONTRACT_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
        resourceCost: RESOURCE_COST_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
        appointments: PROJECT_ROLE_VALUES.map((value) => ({ value, label: APPOINTMENT_NAMES[value].name })),
        appointmentsBySide: {
          contractor: PROJECT_ROLES_BY_SIDE.contractor.map((value) => ({ value, label: APPOINTMENT_NAMES[value].name })),
          employer: PROJECT_ROLES_BY_SIDE.employer.map((value) => ({ value, label: APPOINTMENT_NAMES[value].name })),
        },
      };
    },
    async createProject(payload: Partial<FormData>) {
      try {
        if (!currentUser) {
          NativeBridge.send('addProjectResult', { ok: false, error: 'Not signed in' });
          return;
        }
        const formData: FormData = {
          ...INITIAL_FORM_DATA,
          ...payload,
          projectLocation: { ...EMPTY_PROJECT_LOCATION, ...(payload.projectLocation ?? {}) },
        };
        // serviceType isn't on the native form; derive it from the contractor's
        // billing tier (same rule the web form applies on client selection).
        const contractor = (DataService.getCompanies() as Company[]).find((c) => c.id === formData.clientId);
        formData.serviceType = deriveServiceType(contractor);
        // Mirror the form's required fields (contractor + project manager + employer
        // + estimator + name) so a malformed payload can't create a half-populated
        // project the web UI would have blocked.
        if (
          !formData.projectName.trim() ||
          !formData.clientId ||
          !formData.clientContactId ||
          !formData.mainContractorId ||
          !formData.estimatorContactId
        ) {
          NativeBridge.send('addProjectResult', { ok: false, error: 'Missing required fields' });
          return;
        }
        if (!formData.appointment || !appointmentIsValidForSide(formData.actingFor, formData.appointment)) {
          NativeBridge.send('addProjectResult', { ok: false, error: 'Choose our role' });
          return;
        }
        const newProject = buildNewProjectFromFormData(formData, currentUser);
        const created = await DataService.createProjectAsync(newProject);
        if (!created?.id) {
          NativeBridge.send('addProjectResult', { ok: false, error: 'Create returned no id' });
          return;
        }
        // Folder setup runs in the background (same single-flight dedupe as the
        // web form path); a failure there doesn't fail the create.
        new ProjectFoldersService()
          .createProjectFolders(created.id, created.name, currentUser.id, created.clientId)
          .catch((err) => console.warn('[nativeAddProject] folder setup failed', err));
        NativeBridge.send('addProjectResult', { ok: true, projectId: created.id, projectName: created.name });
      } catch (err) {
        NativeBridge.send('addProjectResult', {
          ok: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    },
  };

  (window as unknown as { __quantisAddProjectData?: NativeAddProjectDataAPI }).__quantisAddProjectData = api;
}
